import React, { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../hooks/useSettings';

const apiBase = process.env.REACT_APP_BACKEND_URL;

const currencyFormatter = (currency) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: currency || 'USD'
});

const fetchJSON = async (url, opts = {}) => {
  const adminToken = localStorage.getItem('admin_token');
  const res = await fetch(`${apiBase}${url}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${adminToken}`,
      ...(opts.headers || {})
    }
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const PartnerWalletAdminTab = () => {
  // Wallet
  const [wallet, setWallet] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(false);

  const { safeSettings } = useSettings();
  const displayTz = safeSettings?.timezone || 'UTC';

  // Format date with timezone (treating DB time as UTC)
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      // Ensure the date is treated as UTC by appending 'Z' if not present
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const d = new Date(utcDateString);
      
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: displayTz
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  // Packages (paged)
  const [packages, setPackages] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [currency, setCurrency] = useState('');
  const [isActive, setIsActive] = useState('');
  const [loadingPkgs, setLoadingPkgs] = useState(false);

  // Package form
  const [showForm, setShowForm] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [pkgForm, setPkgForm] = useState({ name: '', description: '', credits: '', price: '', currency: 'USD', is_active: true, sort_order: 0 });
  const [savingPkg, setSavingPkg] = useState(false);

  // Manual topup form
  const [manualForm, setManualForm] = useState({ package_id: '', direct_credits: '', amount: '', currency: 'USD', reference: '', notes: '' });
  const [processingManual, setProcessingManual] = useState(false);

  // Topups history
  const [topups, setTopups] = useState([]);
  const [topupsPage, setTopupsPage] = useState(1);
  const [topupsTotal, setTopupsTotal] = useState(0);
  const [loadingTopups, setLoadingTopups] = useState(false);

  const loadWallet = async () => {
    try {
      setLoadingWallet(true);
      const data = await fetchJSON('/api/admin/partner/wallet');
      setWallet(data);
    } catch (e) {
      console.error('Failed to load wallet', e);
    } finally {
      setLoadingWallet(false);
    }
  };

  const loadPackages = async () => {
    try {
      setLoadingPkgs(true);
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (q) params.set('q', q);
      if (currency) params.set('currency', currency);
      if (isActive !== '') params.set('is_active', isActive);
      const data = await fetchJSON(`/api/admin/partner/topup-packages/paged?${params.toString()}`);
      setPackages(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Failed to load packages', e);
    } finally {
      setLoadingPkgs(false);
    }
  };

  const loadTopups = async () => {
    try {
      setLoadingTopups(true);
      const params = new URLSearchParams({ page: String(topupsPage), page_size: '10' });
      const data = await fetchJSON(`/api/admin/partner/topups/paged?${params.toString()}`);
      setTopups(data.items || []);
      setTopupsTotal(data.total || 0);
    } catch (e) {
      console.error('Failed to load topups', e);
    } finally {
      setLoadingTopups(false);
    }
  };

  useEffect(() => { 
    loadWallet(); 
  }, []);
  useEffect(() => { loadPackages(); }, [page, pageSize, q, currency, isActive]);
  useEffect(() => { loadTopups(); }, [topupsPage]);

  const resetPkgForm = () => {
    setPkgForm({ name: '', description: '', credits: '', price: '', currency: 'USD', is_active: true, sort_order: 0 });
    setEditingPkg(null);
  };

  const savePackage = async () => {
    try {
      setSavingPkg(true);
      const payload = {
        ...pkgForm,
        credits: Number(pkgForm.credits),
        price: Number(pkgForm.price),
        sort_order: Number(pkgForm.sort_order)
      };
      if (editingPkg) {
        await fetchJSON(`/api/admin/partner/topup-packages/${editingPkg.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await fetchJSON('/api/admin/partner/topup-packages', { method: 'POST', body: JSON.stringify(payload) });
      }
      await loadPackages();
      setShowForm(false);
      resetPkgForm();
    } catch (e) {
      alert('Failed to save package');
      console.error(e);
    } finally {
      setSavingPkg(false);
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await fetchJSON(`/api/admin/partner/topup-packages/${id}`, { method: 'DELETE' });
      await loadPackages();
    } catch (e) {
      alert('Failed to delete package');
      console.error(e);
    }
  };

  const submitManualTopup = async () => {
    try {
      setProcessingManual(true);
      const payload = { ...manualForm };
      if (payload.package_id) {
        delete payload.direct_credits;
        delete payload.amount;
        delete payload.currency;
      } else {
        payload.direct_credits = Number(payload.direct_credits || 0);
        payload.amount = Number(payload.amount || 0);
      }
      await fetchJSON('/api/admin/partner/topups/manual', { method: 'POST', body: JSON.stringify(payload) });
      await loadWallet();
      await loadTopups();
      setManualForm({ package_id: '', direct_credits: '', amount: '', currency: 'USD', reference: '', notes: '' });
      alert('Manual top-up successful');
    } catch (e) {
      alert('Manual top-up failed');
      console.error(e);
    } finally {
      setProcessingManual(false);
    }
  };

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Partner Wallet (Superadmin)</h2>

      {/* Wallet Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Credits Balance</div>
          <div className="text-3xl font-semibold text-gray-900 mt-1">{wallet ? wallet.credits_balance : '—'}</div>
          <div className="text-xs text-gray-400 mt-2">Updated: {formatDate(wallet?.updated_at)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Last Top-up</div>
          <div className="text-lg text-gray-900 mt-1">{formatDate(wallet?.last_topup_at)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Timezone</div>
          <div className="text-lg text-gray-900 mt-1">{displayTz}</div>
        </div>
      </div>

      {/* Packages */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Credit Packages</h3>
          <button className="px-4 py-2 bg-indigo-600 text-white rounded" onClick={() => { resetPkgForm(); setShowForm(true); }}>New Package</button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name/desc" className="border rounded px-3 py-2" />
          <select value={currency} onChange={e => setCurrency(e.target.value)} className="border rounded px-3 py-2">
            <option value="">All Currencies</option>
            <option value="USD">USD</option>
            <option value="INR">INR</option>
          </select>
          <select value={isActive} onChange={e => setIsActive(e.target.value)} className="border rounded px-3 py-2">
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500">
                <th className="p-2">Name</th>
                <th className="p-2">Credits</th>
                <th className="p-2">Price</th>
                <th className="p-2">Currency</th>
                <th className="p-2">Active</th>
                <th className="p-2">Sort</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.map(p => (
                <tr key={p.id} className="border-t">
                  <td className="p-2">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.description || ''}</div>
                  </td>
                  <td className="p-2">{p.credits}</td>
                  <td className="p-2">{currencyFormatter(p.currency).format(p.price)}</td>
                  <td className="p-2">{p.currency}</td>
                  <td className="p-2">{p.is_active ? 'Yes' : 'No'}</td>
                  <td className="p-2">{p.sort_order}</td>
                  <td className="p-2 flex gap-2">
                    <button className="px-3 py-1 text-sm bg-gray-100 rounded" onClick={() => { setEditingPkg(p); setPkgForm({ name: p.name, description: p.description || '', credits: String(p.credits), price: String(p.price), currency: p.currency, is_active: !!p.is_active, sort_order: Number(p.sort_order || 0) }); setShowForm(true); }}>Edit</button>
                    <button className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded" onClick={() => deletePackage(p.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">Total: {total}</div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
            <span className="px-2">Page {page}</span>
            <button disabled={(page * pageSize) >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>

      {/* Manual Top-up */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Manual Top-up</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Use Package</label>
            <select value={manualForm.package_id} onChange={e => setManualForm(f => ({ ...f, package_id: e.target.value }))} className="border rounded px-3 py-2 w-full">
              <option value="">— Direct Credits —</option>
              {packages.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.currency} {p.price})</option>
              ))}
            </select>
          </div>
          {!manualForm.package_id && (
            <>
              <div>
                <label className="block text-sm mb-1">Direct Credits</label>
                <input type="number" value={manualForm.direct_credits} onChange={e => setManualForm(f => ({ ...f, direct_credits: e.target.value }))} className="border rounded px-3 py-2 w-full" />
              </div>
              <div>
                <label className="block text-sm mb-1">Amount</label>
                <input type="number" value={manualForm.amount} onChange={e => setManualForm(f => ({ ...f, amount: e.target.value }))} className="border rounded px-3 py-2 w-full" />
              </div>
              <div>
                <label className="block text-sm mb-1">Currency</label>
                <select value={manualForm.currency} onChange={e => setManualForm(f => ({ ...f, currency: e.target.value }))} className="border rounded px-3 py-2 w-full">
                  <option value="USD">USD</option>
                  <option value="INR">INR</option>
                </select>
              </div>
            </>
          )}
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">Reference</label>
              <input value={manualForm.reference} onChange={e => setManualForm(f => ({ ...f, reference: e.target.value }))} className="border rounded px-3 py-2 w-full" />
            </div>
            <div>
              <label className="block text-sm mb-1">Notes</label>
              <input value={manualForm.notes} onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))} className="border rounded px-3 py-2 w-full" />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={submitManualTopup} disabled={processingManual} className="px-4 py-2 bg-emerald-600 text-white rounded disabled:opacity-50">
            {processingManual ? 'Processing...' : 'Add Credits'}
          </button>
        </div>
      </div>

      {/* Top-ups History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top-ups History</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500">
                <th className="p-2">Created</th>
                <th className="p-2">Credits</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Currency</th>
                <th className="p-2">Status</th>
                <th className="p-2">PG</th>
              </tr>
            </thead>
            <tbody>
              {topups.map(t => (
                <tr key={t.id} className="border-t">
                  <td className="p-2 text-sm text-gray-700">{formatDate(t.created_at)}</td>
                  <td className="p-2">{t.credits}</td>
                  <td className="p-2">{t.amount}</td>
                  <td className="p-2">{t.currency}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      t.status === 'paid' ? 'bg-green-100 text-green-800' :
                      t.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      t.status === 'failed' ? 'bg-red-100 text-red-800' :
                      t.status === 'refunded' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="p-2">{t.pg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">Total: {topupsTotal}</div>
          <div className="flex gap-2">
            <button disabled={topupsPage <= 1} onClick={() => setTopupsPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
            <span className="px-2">Page {topupsPage}</span>
            <button disabled={(topupsPage * 10) >= topupsTotal} onClick={() => setTopupsPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>

      {/* Package Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{editingPkg ? 'Edit Package' : 'New Package'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">Name</label>
                <input value={pkgForm.name} onChange={e => setPkgForm(f => ({ ...f, name: e.target.value }))} className="border rounded px-3 py-2 w-full" />
              </div>
              <div>
                <label className="block text-sm mb-1">Credits</label>
                <input type="number" value={pkgForm.credits} onChange={e => setPkgForm(f => ({ ...f, credits: e.target.value }))} className="border rounded px-3 py-2 w-full" />
              </div>
              <div>
                <label className="block text-sm mb-1">Price</label>
                <input type="number" value={pkgForm.price} onChange={e => setPkgForm(f => ({ ...f, price: e.target.value }))} className="border rounded px-3 py-2 w-full" />
              </div>
              <div>
                <label className="block text-sm mb-1">Currency</label>
                <select value={pkgForm.currency} onChange={e => setPkgForm(f => ({ ...f, currency: e.target.value }))} className="border rounded px-3 py-2 w-full">
                  <option value="USD">USD</option>
                  <option value="INR">INR</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Description</label>
                <textarea value={pkgForm.description} onChange={e => setPkgForm(f => ({ ...f, description: e.target.value }))} className="border rounded px-3 py-2 w-full" rows={3} />
              </div>
              <div>
                <label className="block text-sm mb-1">Active</label>
                <select value={pkgForm.is_active ? 'true' : 'false'} onChange={e => setPkgForm(f => ({ ...f, is_active: e.target.value === 'true' }))} className="border rounded px-3 py-2 w-full">
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Sort Order</label>
                <input type="number" value={pkgForm.sort_order} onChange={e => setPkgForm(f => ({ ...f, sort_order: e.target.value }))} className="border rounded px-3 py-2 w-full" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button className="px-4 py-2 border rounded" onClick={() => { setShowForm(false); resetPkgForm(); }}>Cancel</button>
              <button className="px-4 py-2 bg-indigo-600 text-white rounded" onClick={savePackage} disabled={savingPkg}>{savingPkg ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerWalletAdminTab;